/**
 * ZERBAY cart size module (hz/63): "Change size" for one cart line, in the cart drawer and on the /cart page.
 *
 * The markup is snippets/zerbay-cart-size-change.liquid (hz/61). This module reveals it, toggles the size picker and runs
 * the swap (raise the new size, check stock, remove the old line) as ONE writer inside the cart lock of hz/62
 * (`window.Zerbay.cartLock`, assets/zerbay-cart.js). It never imports that module: one lock per page.
 *
 * Plain ES module, no imports. Horizon declares `const Theme` (not a window property), so routes are read with `typeof`.
 */

/* global Theme */

/**
 * @typedef {object} CartLock
 * @property {(kind: string, writer: (ctx: WriterContext) => Promise<object>, options?: object) => Promise<any>} run
 * @property {boolean} busy
 * @property {string} kind
 * @property {(text: string, row?: Element|null) => void} showError
 * @property {() => { error: string, stock: string }} strings
 * @property {() => void} markSwapEnd
 * @property {() => void} flush
 */

/**
 * @typedef {object} WriterContext
 * @property {AbortSignal} signal
 * @property {(url: string, init?: RequestInit) => Promise<Response>} fetch
 * @property {() => Promise<Cart>} readCart
 * @property {() => Promise<void>} renderViews
 */

/**
 * @typedef {object} CartLine
 * @property {string} key
 * @property {number} variant_id
 * @property {number} quantity
 * @property {Record<string, unknown>|null} [properties]
 * @property {unknown} [selling_plan_allocation]
 * @property {boolean} [has_components]
 * @property {{ parent_key?: string|null, parent?: unknown }|null} [parent_relationship]
 * @property {{ can_remove?: boolean, can_update_quantity?: boolean }|null} [instructions]
 */

/**
 * @typedef {object} Cart
 * @property {CartLine[]} items
 */

/**
 * @typedef {object} Post
 * @property {boolean} ok The answer is a 2xx with a cart JSON.
 * @property {number} status HTTP status, 0 for a network failure.
 * @property {Cart|null} cart
 * @property {string} description Shopify's text of a 422 answer.
 */

/**
 * One size swap, read from the picker markup at click time.
 * @typedef {object} SwapOp
 * @property {string} key Line key of the old line.
 * @property {number} from Variant id of the old line.
 * @property {number} to Variant id of the picked size.
 * @property {number} qty Quantity the markup printed (the swap uses the current one).
 * @property {string} sectionId Section id of the cart view that holds the picker.
 */

const LOCK_WAIT_MS = 5000;
const ANNOUNCE_DELAY_MS = 300;
const READY_CLASS = 'zb-cart-size-ready';
const POINTER_ATTR = 'data-zb-pointer-focus';
const WAS_ATTR = 'data-zb-size-was';
const CONTROLS = '[data-zb-size] button';

/** Set while a pick waits for the lock script, so a second tap does not queue a second swap. */
let picking = false;
/** Whether the last interaction was a pointer press (true) or a key (false). */
let lastWasPointer = false;
/** @type {HTMLElement|null} */
let statusRegion = null;
/** Whether the swap job of this module holds the lock (its release ends the picked chip's busy mark). */
let swapHeld = false;
/** @type {MutationObserver|null} Watches the cart views while a swap runs, so morphed controls are disabled again. */
let controlWatch = null;

/* ------------------------------------------------------------------------------------------------
   Small helpers
   ------------------------------------------------------------------------------------------------ */

/**
 * A locale-aware cart route (Horizon: Theme.routes), with a plain fallback.
 * @param {string} name Key of `Theme.routes`.
 * @param {string} fallback
 * @returns {string}
 */
function route(name, fallback) {
  return typeof Theme !== 'undefined' ? (Theme.routes?.[name] ?? fallback) : fallback;
}

/** @returns {CartLock|null} The cart lock, when its module has already run. */
function currentLock() {
  return window.Zerbay?.cartLock ?? null;
}

/** @returns {boolean} Whether a cart writer holds the lock right now. */
function lockBusy() {
  return currentLock()?.busy === true;
}

/**
 * Waits for the lock (zerbay-cart.js evaluates first in practice); gives up after 5 s.
 * @returns {Promise<CartLock|null>}
 */
function whenLock() {
  const ready = currentLock();
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      document.removeEventListener('zerbay:cart-lock-ready', finish);
      resolve(currentLock());
    };
    const timer = setTimeout(finish, LOCK_WAIT_MS);
    document.addEventListener('zerbay:cart-lock-ready', finish);
  });
}

/**
 * @param {string} sectionId
 * @returns {HTMLElement|null} The cart view with that section id, as it is in the document now.
 */
function viewById(sectionId) {
  return /** @type {HTMLElement|null} */ (
    Array.from(document.querySelectorAll('cart-items-component')).find(
      (view) => /** @type {HTMLElement} */ (view).dataset.sectionId === sectionId
    ) ?? null
  );
}

/**
 * @param {ParentNode|null} view
 * @param {string} key
 * @returns {HTMLTableRowElement|null} The line row with that key.
 */
function rowByKey(view, key) {
  if (!view || !key) return null;
  return /** @type {HTMLTableRowElement|null} */ (
    Array.from(view.querySelectorAll('tr[data-key]')).find((row) => /** @type {HTMLElement} */ (row).dataset.key === key) ?? null
  );
}

/* ------------------------------------------------------------------------------------------------
   Focus and announcement (CART-41, CART-42)
   ------------------------------------------------------------------------------------------------ */

/**
 * Focuses an element from script. After a pointer interaction the element is marked so CSS shows no ring.
 * @param {HTMLElement|null} element
 * @param {boolean} [preventScroll] True for the legend (it must not move the drawer); the new line's toggle may scroll into view.
 */
function focusScripted(element, preventScroll = false) {
  if (!element) return;
  if (lastWasPointer) element.setAttribute(POINTER_ATTR, '');
  element.focus({ preventScroll });
}

/**
 * Removes the pointer mark when the element loses focus.
 * @param {Event} event
 */
function onBlur(event) {
  const target = event.target;
  if (target instanceof Element && target.hasAttribute(POINTER_ATTR)) target.removeAttribute(POINTER_ATTR);
}

/** @returns {Element} The open modal dialog (the drawer below 990 px; all else is inert), else the body. */
function liveHost() {
  try {
    return document.querySelector('dialog:modal') ?? document.body;
  } catch {
    return document.querySelector('dialog[open]') ?? document.body;
  }
}

/** @returns {HTMLElement} The one polite live region, created on first use and moved into the open modal when there is one. */
function liveRegion() {
  const host = liveHost();
  if (!statusRegion) {
    statusRegion = document.createElement('div');
    statusRegion.className = 'visually-hidden';
    statusRegion.setAttribute('role', 'status');
    statusRegion.setAttribute('aria-live', 'polite');
  }
  if (statusRegion.parentElement !== host) host.append(statusRegion);
  return statusRegion;
}

/**
 * Announces a text politely: clears the region, then sets the text 300 ms later (VoiceOver on iOS misses text that is
 * set right after the region was inserted).
 * @param {string} text
 */
function announce(text) {
  if (!text) return;
  const region = liveRegion();
  region.textContent = '';
  setTimeout(() => {
    region.textContent = text;
  }, ANNOUNCE_DELAY_MS);
}

/* ------------------------------------------------------------------------------------------------
   Picker: toggle, Escape, controls while a swap runs
   ------------------------------------------------------------------------------------------------ */

/**
 * Opens or closes one picker.
 * @param {Element} box The `[data-zb-size]` wrapper.
 * @param {boolean} open
 */
function setOpen(box, open) {
  const toggle = box.querySelector('[data-zb-size-toggle]');
  const set = /** @type {HTMLElement|null} */ (box.querySelector('[data-zb-size-set]'));
  if (!toggle || !set) return;
  set.hidden = !open;
  toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
}

/**
 * Toggles a picker; opening closes every other open picker of the same cart view.
 * @param {Element} box
 */
function toggle(box) {
  const set = /** @type {HTMLElement|null} */ (box.querySelector('[data-zb-size-set]'));
  if (!set) return;
  const open = set.hidden;
  if (open) {
    const scope = box.closest('cart-items-component') ?? document;
    for (const other of scope.querySelectorAll('[data-zb-size]')) {
      if (other !== box) setOpen(other, false);
    }
  }
  setOpen(box, open);
}

/**
 * Escape inside an open picker closes it and keeps the drawer open.
 * @param {KeyboardEvent} event
 */
function onKeydown(event) {
  lastWasPointer = false;
  if (event.key !== 'Escape' || event.isComposing || currentLock()?.kind === 'swap') return;
  const box = document.activeElement?.closest('[data-zb-size]');
  const set = /** @type {HTMLElement|null} */ (box?.querySelector('[data-zb-size-set]') ?? null);
  if (!box || !set || set.hidden) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  setOpen(box, false);
  focusScripted(/** @type {HTMLElement|null} */ (box.querySelector('[data-zb-size-toggle]')));
}

/** Marks the last interaction as a pointer press. */
function onPointerDown() {
  lastWasPointer = true;
}

/**
 * Disables every chip and toggle, keeping the server state to restore. A control a morph has just replaced (or whose
 * disabled state a morph reset) records its fresh server state.
 */
function disableControls() {
  for (const control of /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll(CONTROLS))) {
    if (control.disabled && control.hasAttribute(WAS_ATTR)) continue;
    control.setAttribute(WAS_ATTR, control.disabled ? '1' : '0');
    control.disabled = true;
  }
}

/** Restores the controls disabled by `disableControls` and clears the busy mark of the picked chip. */
function restoreControls() {
  controlWatch?.disconnect();
  controlWatch = null;
  for (const control of /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll(`[${WAS_ATTR}]`))) {
    control.disabled = control.getAttribute(WAS_ATTR) === '1';
    control.removeAttribute(WAS_ATTR);
  }
  for (const chip of document.querySelectorAll('[data-zb-size] [aria-busy]')) chip.removeAttribute('aria-busy');
}

/** Disables the controls now and again after every morph while the swap holds the lock. */
function holdControls() {
  disableControls();
  controlWatch?.disconnect();
  controlWatch = new MutationObserver(disableControls);
  controlWatch.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
}

/**
 * The lock's busy/free events: the controls follow a swap job.
 * @param {CustomEvent<{ busy: boolean, kind: string }>} event
 */
function onLockEvent(event) {
  if (event.detail?.busy) {
    if (event.detail.kind === 'swap') {
      swapHeld = true;
      holdControls();
    }
  } else if (swapHeld) {
    swapHeld = false;
    restoreControls();
  }
}

/**
 * A cart form must not be submitted while a swap runs.
 * @param {SubmitEvent} event
 */
function onSubmit(event) {
  if (currentLock()?.kind !== 'swap') return;
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (form?.id !== 'cart-form' && event.submitter?.getAttribute('form') !== 'cart-form') return;
  event.preventDefault();
  event.stopImmediatePropagation();
}

/* ------------------------------------------------------------------------------------------------
   Cart data helpers
   ------------------------------------------------------------------------------------------------ */

/**
 * @param {Cart} cart
 * @param {number} variantId
 * @returns {CartLine[]} Lines of one variant.
 */
function linesOf(cart, variantId) {
  return cart.items.filter((item) => Number(item.variant_id) === variantId);
}

/**
 * @param {Cart} cart
 * @param {number} variantId
 * @returns {number} Total quantity of one variant.
 */
function quantityOf(cart, variantId) {
  return linesOf(cart, variantId).reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * @param {Cart} cart
 * @returns {string} A comparable fingerprint of the lines (key, variant, quantity).
 */
function signature(cart) {
  return cart.items
    .map((item) => `${item.key}:${item.variant_id}:${item.quantity}`)
    .sort()
    .join('|');
}

/**
 * A line the size change may touch: no properties, selling plan, bundle link, and quantity and removal allowed.
 * @param {CartLine|undefined} item
 * @returns {boolean}
 */
function isPlainLine(item) {
  if (!item) return false;
  if (item.selling_plan_allocation || (item.properties && Object.keys(item.properties).length > 0)) return false;
  if (item.has_components || item.parent_relationship?.parent_key || item.parent_relationship?.parent) return false;
  return item.instructions?.can_remove !== false && item.instructions?.can_update_quantity !== false;
}

/**
 * The line to remove: found by its current key (then it must still be what the swap saw: same variant, same quantity,
 * plain), else the only PLAIN line of the old variant with the same quantity (its key changed meanwhile).
 * @param {Cart} cart
 * @param {SwapOp} op
 * @param {number} quantity
 * @returns {CartLine|null}
 */
function findOldLine(cart, op, quantity) {
  const byKey = cart.items.find((item) => item.key === op.key);
  if (byKey) {
    return Number(byKey.variant_id) === op.from && byKey.quantity === quantity && isPlainLine(byKey) ? byKey : null;
  }
  const plain = linesOf(cart, op.from).filter(isPlainLine);
  return plain.length === 1 && plain[0].quantity === quantity ? plain[0] : null;
}

/**
 * The old line is removed when its key is gone AND the old variant's total dropped by exactly q since the start
 * (an unrelated line of the same variant, with properties or a selling plan, does not matter).
 * @param {Cart} cart
 * @param {SwapOp} op
 * @param {SwapState} state
 * @returns {boolean}
 */
function isOldRemoved(cart, op, state) {
  if (cart.items.some((item) => item.key === op.key)) return false;
  return quantityOf(cart, op.from) === quantityOf(state.start, op.from) - state.quantity;
}

/**
 * @param {Cart} cart
 * @param {SwapOp} op
 * @param {number} want
 * @returns {boolean} Exactly one line of the picked size, with exactly the wanted quantity.
 */
function isExactTarget(cart, op, want) {
  const lines = linesOf(cart, op.to);
  return lines.length === 1 && lines[0].quantity === want;
}

/**
 * @param {string} text
 * @returns {number|null} The first number in Shopify's message ("Only 2 items were added ...").
 */
function firstNumber(text) {
  const match = typeof text === 'string' ? text.match(/\d+/) : null;
  return match ? Number(match[0]) : null;
}

/* ------------------------------------------------------------------------------------------------
   The swap writer (runs inside the cart lock)
   ------------------------------------------------------------------------------------------------ */

/**
 * POSTs JSON to change.js / update.js through the lock's watchdog-armed fetch. A network failure is an answer with
 * status 0 (the caller reads the cart next); an aborted job (watchdog) is rethrown.
 * @param {WriterContext} ctx
 * @param {'change'|'update'} name
 * @param {object} body
 * @returns {Promise<Post>}
 */
async function postJson(ctx, name, body) {
  const url = `${route(`cart_${name}_url`, `/cart/${name}`)}.js`;
  try {
    const response = await ctx.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    /** @type {any} */
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    const cart = data && Array.isArray(data.items) ? data : null;
    return {
      ok: response.ok && cart !== null,
      status: response.status,
      cart,
      description: typeof data?.description === 'string' ? data.description : '',
    };
  } catch (error) {
    if (ctx.signal?.aborted) throw error;
    return { ok: false, status: 0, cart: null, description: '' };
  }
}

/**
 * What the swap knows once it has read the cart; kept outside the steps so a failed step can still be reconciled.
 * @typedef {object} SwapState
 * @property {Cart} start S, the cart before any write.
 * @property {number} quantity q, the old line's quantity.
 * @property {number} n The picked size's quantity in S.
 * @property {number} want n + q.
 */

/**
 * Returns a refused swap. The views are re-rendered when the cart differs from the one the swap started with, and
 * always for GONE (its views show a line the server no longer has) and STOCK (the refused chip shows its new state).
 * @param {WriterContext} ctx
 * @param {string} code 'STOCK' | 'ERROR' | 'GONE'
 * @param {Cart} cart
 * @param {Cart} start The cart read at the start (S).
 * @returns {Promise<{ ok: false, code: string, cart: Cart }>}
 */
async function refuse(ctx, code, cart, start) {
  if (code === 'GONE' || code === 'STOCK' || signature(cart) !== signature(start)) {
    try {
      await ctx.renderViews();
    } catch {
      /* the lock waits for the views against `cart` and recovers; a failed render never changes the reason */
    }
  }
  return { ok: false, code, cart };
}

/**
 * Raises the picked size to n + q. An absent size is added by update.js (no stock check) and then set to q by
 * change.js, which checks stock (P4-0 / P4-0b).
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {CartLine|undefined} target The existing line of the picked size.
 * @param {number} quantity q
 * @param {number} want n + q
 * @returns {Promise<{ answer: Post, checked: boolean, checkedFrom: number }>}
 */
async function raiseNewSize(ctx, op, target, quantity, want) {
  if (target) {
    const answer = await postJson(ctx, 'change', { id: target.key, quantity: want });
    return { answer, checked: true, checkedFrom: target.quantity };
  }
  const added = await postJson(ctx, 'update', { updates: { [op.to]: quantity } });
  const lines = added.cart ? linesOf(added.cart, op.to) : [];
  if (added.ok && lines.length === 1 && lines[0].quantity === quantity) {
    const answer = await postJson(ctx, 'change', { id: lines[0].key, quantity });
    return { answer, checked: true, checkedFrom: quantity };
  }
  return { answer: added, checked: false, checkedFrom: 0 };
}

/**
 * Sets the picked size to `quantity`. Lowering uses change.js by key; raising uses update.js (no stock check, so a
 * restore is not refused by a stock cap).
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {Cart} cart The cart the line is read from.
 * @param {number} quantity
 * @returns {Promise<number>} The HTTP status (0 = network failure, -1 = no request could be made).
 */
async function setTarget(ctx, op, cart, quantity) {
  const lines = linesOf(cart, op.to);
  if (lines.length > 1) return -1;
  if (quantity > quantityOf(cart, op.to)) {
    return (await postJson(ctx, 'update', { updates: { [op.to]: quantity } })).status;
  }
  if (lines.length === 0) return -1;
  return (await postJson(ctx, 'change', { id: lines[0].key, quantity })).status;
}

/**
 * Brings the picked size back to n when this swap moved it: take back what it added (n < got <= want), restore what a
 * cap removed (got < n); an external addition (got > want) is left alone. Each request is confirmed by a read; one that
 * failed at network level and left the quantity unchanged is sent once more.
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {SwapState} state
 * @param {Cart} cart
 * @returns {Promise<{ cart: Cart, clean: boolean, touched: boolean }>} `clean`: the quantity is n; `touched`: a request was attempted (also true when setTarget returned -1 and none was sent).
 */
async function compensate(ctx, op, state, cart) {
  let now = cart;
  let touched = false;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const got = quantityOf(now, op.to);
    if (got === state.n || got > state.want) break;
    touched = true;
    const status = await setTarget(ctx, op, now, state.n);
    now = await ctx.readCart();
    if (status !== 0) break;
  }
  return { cart: now, clean: quantityOf(now, op.to) === state.n, touched };
}

/**
 * The swap cannot confirm what the server holds: the lock suspends the queue, shows the error and reloads.
 * @param {Cart} [cart] The last cart read.
 * @returns {{ ok: false, code: 'UNRECONCILED', cart: Cart|undefined }}
 */
function unreconciled(cart) {
  return { ok: false, code: 'UNRECONCILED', cart };
}

/**
 * Finishes a swap that did not go straight through, from a cart that was just read. The old line decides first:
 * - gone (key gone, old variant down by q): target exactly n + q -> success; anything else -> ERROR with the read cart,
 *   no reset of the target (an external change must not make the swap delete pairs);
 * - still there: put the target back to n (compensate), then ERROR; a compensation that cannot be confirmed -> UNRECONCILED.
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {{ newKey: string|null }} outcome
 * @param {SwapState} state
 * @param {Cart} cart
 * @returns {Promise<object>}
 */
async function reconcile(ctx, op, outcome, state, cart) {
  if (isOldRemoved(cart, op, state)) {
    if (isExactTarget(cart, op, state.want)) return succeed(ctx, op, outcome, cart);
    return refuse(ctx, 'ERROR', cart, state.start);
  }
  const { cart: after, clean, touched } = await compensate(ctx, op, state, cart);
  if (touched && !clean) return unreconciled(after);
  return refuse(ctx, 'ERROR', after, state.start);
}

/**
 * A completed swap: remember the new key first, then re-render. A failed re-render does not undo the swap (the lock's
 * RENDER DONE wait and recovery handle the views).
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {{ newKey: string|null }} outcome
 * @param {Cart} cart C3
 * @returns {Promise<object>}
 */
async function succeed(ctx, op, outcome, cart) {
  outcome.newKey = linesOf(cart, op.to)[0]?.key ?? null;
  try {
    await ctx.renderViews();
  } catch {
    /* the views are verified against `cart` by the lock */
  }
  return { ok: true, cart, newKey: outcome.newKey };
}

/**
 * Removes the old line (one retry), reading the cart after each request.
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {SwapState} state
 * @param {Cart} cart C1
 * @returns {Promise<Cart>} C3
 */
async function removeOldLine(ctx, op, state, cart) {
  let now = cart;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (isOldRemoved(now, op, state)) break;
    const line = findOldLine(now, op, state.quantity);
    if (!line) break;
    await postJson(ctx, 'change', { id: line.key, quantity: 0 });
    now = await ctx.readCart();
  }
  return now;
}

/**
 * The raise fell short. Take back what this swap added (or restore what a cap removed), read C2, and say STOCK only
 * when the stock check itself refused (change.js capped the line, or answered 422 and Shopify set the line to exactly
 * the quantity its message names) and the cart is back to S.
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {SwapState} state
 * @param {Cart} after C1
 * @param {{ answer: Post, checked: boolean, checkedFrom: number }} raise
 * @returns {Promise<object>}
 */
async function stockOrError(ctx, op, state, after, raise) {
  const { answer, checked, checkedFrom } = raise;
  const got = quantityOf(after, op.to);
  const confirmed = answer.ok && answer.cart ? quantityOf(answer.cart, op.to) : null;
  const capped = checked && confirmed !== null && confirmed < state.want;
  const refused =
    checked &&
    answer.status === 422 &&
    got >= state.n &&
    got < state.want &&
    got !== checkedFrom &&
    firstNumber(answer.description) === got;
  const { cart: restored, clean, touched } = await compensate(ctx, op, state, after);
  if (touched && !clean) return unreconciled(restored);
  const stock = (capped || refused) && got >= state.n && clean && signature(restored) === signature(state.start);
  return refuse(ctx, stock ? 'STOCK' : 'ERROR', restored, state.start);
}

/**
 * The swap steps (spec 3.3). They record `state` as soon as S is known, so `runSwap` can reconcile after a failed step.
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {{ newKey: string|null }} outcome
 * @param {{ state: SwapState|null }} holder
 * @returns {Promise<object>}
 */
async function swapSteps(ctx, op, outcome, holder) {
  const start = await ctx.readCart();
  const old = start.items.find((item) => item.key === op.key);
  if (!old) return refuse(ctx, 'GONE', start, start);
  const targets = linesOf(start, op.to);
  const usable = Number(old.variant_id) === op.from && op.to !== op.from && isPlainLine(old);
  if (!usable || targets.length > 1 || (targets.length === 1 && !isPlainLine(targets[0]))) {
    return refuse(ctx, 'ERROR', start, start);
  }

  const n = targets.length ? targets[0].quantity : 0;
  const state = { start, quantity: old.quantity, n, want: n + old.quantity };
  holder.state = state;
  const raise = await raiseNewSize(ctx, op, targets[0], state.quantity, state.want);
  const after = await ctx.readCart();

  const answered = raise.answer.cart !== null && isExactTarget(raise.answer.cart, op, state.want);
  if (!(raise.checked && raise.answer.ok && answered && isExactTarget(after, op, state.want))) {
    return stockOrError(ctx, op, state, after, raise);
  }
  return reconcile(ctx, op, outcome, state, await removeOldLine(ctx, op, state, after));
}

/**
 * Wraps the swap. A step that throws (a read or request failed at network level) is reconciled once the cart can be
 * read again; when it cannot be read at all, SERVICE_UNAVAILABLE (nothing was written yet) lets the lock read once more
 * and reload if it must, and UNRECONCILED (a write may have landed) ends in the lock's suspend and reload.
 * @param {WriterContext} ctx
 * @param {SwapOp} op
 * @param {{ newKey: string|null }} outcome
 * @returns {Promise<object>}
 */
async function runSwap(ctx, op, outcome) {
  /** @type {{ state: SwapState|null }} */
  const holder = { state: null };
  try {
    return await swapSteps(ctx, op, outcome, holder);
  } catch {
    try {
      const cart = await ctx.readCart();
      if (holder.state) return await reconcile(ctx, op, outcome, holder.state, cart);
      return await refuse(ctx, 'ERROR', cart, cart);
    } catch {
      // A write may already have landed (state is set) and the cart cannot be read: the lock suspends and reloads.
      if (holder.state) return unreconciled();
      return { ok: false, code: 'SERVICE_UNAVAILABLE', message: null };
    }
  }
}

/* ------------------------------------------------------------------------------------------------
   Pick: the click handler around the lock
   ------------------------------------------------------------------------------------------------ */

/**
 * Reads the swap from the picker markup.
 * @param {HTMLElement} box
 * @param {HTMLElement} chip
 * @returns {SwapOp|null}
 */
function readOp(box, chip) {
  const key = box.dataset.zbSizeKey ?? '';
  const op = {
    key,
    from: Number(box.dataset.zbSizeFrom),
    to: Number(chip.dataset.zbSizeTo),
    qty: Number(box.dataset.zbSizeQty),
    sectionId: box.closest('cart-items-component')?.getAttribute('data-section-id') ?? '',
  };
  return key && op.to > 0 && op.from > 0 ? op : null;
}

/**
 * Without a lock (script missing) the error still shows in the line's own error cell.
 * @param {Element} box
 */
function showErrorWithoutLock(box) {
  const strings = /** @type {HTMLElement|null} */ (document.querySelector('[data-zb-cart-strings]'));
  const text = strings?.dataset.error ?? '';
  const row = box.closest('tr[data-key]');
  const view = row?.closest('cart-items-component');
  if (!text || !row || !view) return;
  const number = Array.from(view.querySelectorAll('tr[data-key]')).indexOf(row) + 1;
  const cell = view.querySelector(`[ref="cartItemError-${number}"]`);
  const container = view.querySelector(`[ref="cartItemErrorContainer-${number}"]`);
  if (!cell || !container) return;
  cell.textContent = text;
  container.classList.remove('hidden');
}

/**
 * After a successful swap: focus the new line's "Change size" (its title when there is none) and read the new variant.
 * @param {SwapOp} op
 * @param {string|null} newKey
 */
function afterSuccess(op, newKey) {
  const row = rowByKey(viewById(op.sectionId) ?? document, newKey ?? '');
  if (!row) return;
  const target = /** @type {HTMLElement|null} */ (
    row.querySelector('[data-zb-size-toggle]') ?? row.querySelector('.cart-items__title')
  );
  const variant = row.querySelector('.zb-cart-variant');
  if (target && variant) {
    if (!variant.id) variant.id = `zb-variant-${newKey ?? ''}`.replace(/[^\w-]/g, '-');
    target.setAttribute('aria-describedby', variant.id);
  }
  focusScripted(target);
  announce((variant?.textContent ?? '').replace(/\s+/g, ' ').trim());
}

/**
 * After a refused or failed swap: the message in the old line (or the bottom notice), focus on the old line's toggle.
 * @param {CartLock} lock
 * @param {SwapOp} op
 * @param {{ code?: string }} result
 */
function afterFailure(lock, op, result) {
  const row = rowByKey(viewById(op.sectionId) ?? document, op.key);
  const text = result.code === 'STOCK' ? lock.strings().stock : lock.strings().error;
  lock.showError(text, row);
  focusScripted(/** @type {HTMLElement|null} */ (row?.querySelector('[data-zb-size-toggle]') ?? null));
}

/**
 * Runs one pick: focus parks on the group legend (a disabled chip cannot keep focus), the swap runs in the lock.
 * @param {HTMLElement} box
 * @param {HTMLElement} chip
 */
async function pick(box, chip) {
  const op = readOp(box, chip);
  if (!op || picking || lockBusy()) return;
  picking = true;
  try {
    const lock = await whenLock();
    if (!lock) {
      showErrorWithoutLock(box);
      return;
    }
    if (lock.busy) return;
    chip.setAttribute('aria-busy', 'true');
    if (!lastWasPointer) focusScripted(/** @type {HTMLElement|null} */ (box.querySelector('.zb-size__legend')), true);
    lock.flush();
    const outcome = { newKey: /** @type {string|null} */ (null) };
    const result = await lock.run('swap', (ctx) => runSwap(ctx, op, outcome));
    lock.markSwapEnd();
    if (result?.ok) afterSuccess(op, outcome.newKey);
    else afterFailure(lock, op, result ?? {});
  } finally {
    picking = false;
  }
}

/**
 * Delegated click: toggle a picker, or pick a size.
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (!(event.target instanceof Element)) return;
  const control = event.target.closest('[data-zb-size-toggle], [data-zb-size-to]');
  const box = /** @type {HTMLElement|null} */ (control?.closest('[data-zb-size]') ?? null);
  if (!control || !box) return;
  event.preventDefault();
  if (lockBusy() || picking) return;
  if (control.hasAttribute('data-zb-size-toggle')) toggle(box);
  else if (!(control instanceof HTMLButtonElement && control.disabled)) pick(box, /** @type {HTMLElement} */ (control));
}

/* ------------------------------------------------------------------------------------------------
   Start
   ------------------------------------------------------------------------------------------------ */

/** Binds the delegated listeners once and reveals the picker markup (CSS keys on the root class). */
function start() {
  window.Zerbay = window.Zerbay || {};
  window.Zerbay.cartSize = Object.freeze({ version: 1 });
  document.documentElement.classList.add(READY_CLASS);
  document.addEventListener('click', onClick);
  document.addEventListener('zerbay:cart-lock', /** @type {EventListener} */ (onLockEvent));
  window.addEventListener('submit', /** @type {EventListener} */ (onSubmit), { capture: true });
  window.addEventListener('keydown', onKeydown, { capture: true });
  window.addEventListener('pointerdown', onPointerDown, { capture: true });
  window.addEventListener('blur', onBlur, { capture: true });
}

if (!window.Zerbay?.cartSize) start();
